import moment, {Moment} from 'moment';
import {AttendanceTrackerSession} from '../../backend/responses/attendance-tracker-session';
import {
  ATTENDANCE_DAY_PERIOD_START_HOURS,
  ATTENDANCE_OPEN_SESSION_OVERDUE_HOURS,
  AttendanceDayPeriod,
} from '../constants/attendance-tracker';

export type AttendanceSessionMovement = 'entry' | 'exit';
export type AttendanceSessionStatus = 'open' | 'closed' | 'voided';

/** Filters of the work sessions. A filter without values does not filter. */
export interface AttendanceSessionsFilters {
  movements: string[];
  status: string[];
  periods: string[];
}

/** Period of the day of a date, by its hour (in the time zone of the device) */
export const getDayPeriod = (date: string | Date): AttendanceDayPeriod => {
  const hour = moment(date).hour();
  const {morning, afternoon, night} = ATTENDANCE_DAY_PERIOD_START_HOURS;

  if (hour >= night || hour < morning) return 'night';
  if (hour >= afternoon) return 'afternoon';
  return 'morning';
};

/**
 * Seconds of a session. Closed sessions have the duration set by the server. In open sessions it is the
 * time elapsed since the entry until now, taking in account the difference to the clock of the server.
 */
export const getSessionDurationSeconds = (
  session: AttendanceTrackerSession,
  now: Moment
): number => {
  if (session.status === 'closed' && session.durationSeconds !== undefined) {
    return session.durationSeconds;
  }

  return Math.max(0, now.diff(moment(session.entry.registeredAt), 'seconds'));
};

/** An open session for too long is probably a forgotten exit, and not a long shift */
export const isSessionOverdue = (session: AttendanceTrackerSession, now: Moment): boolean =>
  !session.voided &&
  session.status === 'open' &&
  getSessionDurationSeconds(session, now) > ATTENDANCE_OPEN_SESSION_OVERDUE_HOURS * 3600;

export type AttendanceSessionState = 'running' | 'overdue' | 'closed' | 'voided';

/** How a session is shown: voided, still running, running for too long, or finished */
export const getSessionState = (
  session: AttendanceTrackerSession,
  now: Moment
): AttendanceSessionState =>
  session.voided
    ? 'voided'
    : isSessionOverdue(session, now)
      ? 'overdue'
      : session.status === 'open'
        ? 'running'
        : 'closed';

/** Formats seconds as "8 h 08 min", "3 h" or "45 min" */
export const formatSessionDuration = (seconds: number): string => {
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} min`;
  return `${hours} h ${String(minutes).padStart(2, '0')} min`;
};

/** If the entry or the exit of the session was in the day */
export const hasMovementInDay = (
  session: AttendanceTrackerSession,
  movement: AttendanceSessionMovement,
  day: Moment
): boolean => {
  const point = movement === 'entry' ? session.entry : session.exit;
  return !!point && moment(point.registeredAt).isSame(day, 'day');
};

/** Filters the sessions of a day */
export const filterSessions = <T extends AttendanceTrackerSession>(
  sessions: T[],
  filters: AttendanceSessionsFilters,
  day: Moment
): T[] =>
  sessions.filter((session) => {
    const movementMatch =
      filters.movements.length === 0 ||
      filters.movements.some((movement) =>
        hasMovementInDay(session, movement as AttendanceSessionMovement, day)
      );
    // A voided session is neither open nor closed
    const statusMatch =
      filters.status.length === 0 ||
      filters.status.includes(session.voided ? 'voided' : session.status);
    const periodMatch =
      filters.periods.length === 0 ||
      filters.periods.includes(getDayPeriod(session.entry.registeredAt));

    return movementMatch && statusMatch && periodMatch;
  });

/**
 * Seconds of a session that were in a day. A session that crosses midnight is counted in both days, but
 * only the part of each day. Open sessions go on until now.
 */
export const getSessionSecondsInDay = (
  session: AttendanceTrackerSession,
  now: Moment,
  day: Moment
): number => {
  // A voided session is not time worked
  if (session.voided) {
    return 0;
  }

  const start = moment(session.entry.registeredAt);
  const end = session.exit ? moment(session.exit.registeredAt) : now;
  const dayStart = day.clone().startOf('day');
  const dayEnd = day.clone().endOf('day');

  const from = moment.max(start, dayStart);
  const to = moment.min(end, dayEnd);

  return Math.max(0, to.diff(from, 'seconds'));
};

/**
 * Date and time of a record, to the second, in the time zone where it was registered ("26/10/2026 21:49:33").
 * The time zone is also returned when it is not the one of the device that is showing it.
 */
export const formatRecordDateTime = (
  date: string,
  timeZone: string,
  deviceTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): {text: string; timeZone?: string} => {
  let zone = timeZone;
  let parts: Record<string, string>;

  const getParts = (zoneName: string): Record<string, string> =>
    Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: zoneName,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date(date))
        .map(({type, value}) => [type, value])
    );

  try {
    parts = getParts(zone);
  } catch {
    // An unknown time zone: use the one of the device
    zone = deviceTimeZone;
    parts = getParts(zone);
  }

  return {
    text: `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`,
    timeZone: zone !== deviceTimeZone ? zone : undefined,
  };
};
